/**
 * WhatsApp Utility Functions
 * Handles WhatsApp number rotation for load balancing between sales agents
 * Version: 2.1 - Updated Dec 2025 with 3 numbers + Google Ads conversion tracking
 */

// WhatsApp numbers for rotation - Currently set to single number
const WHATSAPP_NUMBERS = [
  '201080639209'   // Primary contact
];

// Default message for G Developments inquiries
const DEFAULT_MESSAGE = "Hello, I'm interested in G Developments projects and would like more details.";

// Storage key for round-robin index - using new key to reset any old cached values
const STORAGE_KEY = 'gdev_wa_idx_v2';

/**
 * Track WhatsApp click as Google Ads conversion
 */
const trackWhatsAppConversion = () => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-16611777229/WHATSAPP_CLICK'
    });
  }
};

/**
 * Get the next WhatsApp number using round-robin rotation
 * Guarantees exactly equal distribution (33.33% each) over time
 * @returns {string} WhatsApp number without + prefix
 */
export const getRandomWhatsAppNumber = () => {
  let currentIndex = 0;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      currentIndex = parseInt(stored, 10);
      if (isNaN(currentIndex) || currentIndex < 0 || currentIndex >= WHATSAPP_NUMBERS.length) {
        currentIndex = 0;
      }
    }
  } catch {
    currentIndex = 0;
  }
  
  const selectedNumber = WHATSAPP_NUMBERS[currentIndex];
  const nextIndex = (currentIndex + 1) % WHATSAPP_NUMBERS.length;
  
  try {
    localStorage.setItem(STORAGE_KEY, nextIndex.toString());
  } catch {
    // Silent fail
  }
  
  // Track conversion on number selection (means click happened)
  trackWhatsAppConversion();
  
  return selectedNumber;
};

/**
 * Generate a WhatsApp URL with random number selection
 * @param {string} message - The pre-filled message (will be URL encoded)
 * @returns {string} Full WhatsApp URL (wa.me format)
 */
export const getWhatsAppUrl = (message = DEFAULT_MESSAGE) => {
  const number = getRandomWhatsAppNumber();
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${number}?text=${encodedMessage}`;
};

/**
 * Open WhatsApp with a random number
 * Works on both desktop (WhatsApp Web) and mobile (WhatsApp App)
 * @param {string} message - The pre-filled message
 */
export const openWhatsApp = (message) => {
  const url = getWhatsAppUrl(message);
  window.open(url, '_blank');
};

export default {
  getRandomWhatsAppNumber,
  getWhatsAppUrl,
  openWhatsApp,
  WHATSAPP_NUMBERS,
  trackWhatsAppConversion
};
